import { sendEvent, makeEventTrack } from 'src/utils/restructure/analytics'
import type {
  GalleryImageMobileProps,
  Video,
  Image as TImage,
} from 'src/components/restructure/product/sections/GalleryImage/types/gallery-image'
import Carousel from 'src/components/restructure/common/Carousel/Carousel'
import { GalleryMovie } from 'src/components/restructure/product/sections/GalleryImage/GalleryMovie/GalleryMovie'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { useState, useContext } from 'react'

import { GallerySkeleton } from '../GallerySkeleton'
import { FullScreenGallery } from '../FullScreenGallery/FullScreenGallery'
import { ButtonMorePictures } from '../ButtonMorePictures'
import { WishListButton } from '../../Sidebar/ProductTitle/WishListButton'
import { GalleryFlags, PreSaleFlag } from '../GalleryFlags'
import { ProductContext } from '../../../contexts'
import { getProductBenefits } from '../../GalleryProductBenefits/utils'
import { GalleryProductBenefits } from '../../GalleryProductBenefits'
import './index.scss'

const STYLES = {
  container: 'gallery-mobile block restructure-small-desktop:hidden',
  containerWithPeek:
    'gallery-mobile gallery-mobile--first-fold-peek block restructure-small-desktop:hidden',
  root: 'relative',
  preSaleFlag: 'absolute top-0 left-0 z-10',
  wishlistButton: 'absolute top-4 right-4 z-10',
  dotsContainer:
    'gallery-mobile-first-fold-dots absolute left-4 z-10 flex items-center gap-[3.5px] justify-start',
  dotActive: 'h-[5px] w-[13px] bg-[#15181B] border-0 p-0 cursor-default',
  dotInactive: 'h-[5px] w-[13px] bg-[#D0D0D0] border-0 p-0 cursor-default',
  morePicturesDefault: 'absolute bottom-4 right-4 z-10',
  morePicturesRevamp: 'absolute bottom-4 left-1/2 -translate-x-1/2 z-10',
} as const

const DOTS_MAX_TRACK_WIDTH_PX = 80
const DOT_GAP_PX = 3.5
const DOT_MAX_WIDTH_PX = 13
const DOT_MIN_WIDTH_PX = 4

export const GalleryImageMobile = ({
  images,
  video,
  isPresale = false,
  isFirstFoldRevampEnabled = false,
}: GalleryImageMobileProps) => {
  const { openModal, closeModal } = useModalSheet()
  const [selectedImageForClick, setSelectedImageForClick] = useState(0)
  const { currentSku } = useContext(ProductContext)
  const productBenefitsArray = getProductBenefits(currentSku?.productBenefits)
  const filteredBenefits = productBenefitsArray.filter(
    (benefit) => benefit.image
  )

  const morePicturesClassName = isFirstFoldRevampEnabled
    ? STYLES.morePicturesRevamp
    : STYLES.morePicturesDefault

  const galleryContainerClassName = isFirstFoldRevampEnabled
    ? STYLES.containerWithPeek
    : STYLES.container

  const getDotWidthPx = (totalItems: number) => {
    if (totalItems <= 1) {
      return DOT_MAX_WIDTH_PX
    }

    const availableWidth =
      DOTS_MAX_TRACK_WIDTH_PX - (totalItems - 1) * DOT_GAP_PX

    if (availableWidth <= 0) {
      return DOT_MIN_WIDTH_PX
    }

    return Math.max(
      DOT_MIN_WIDTH_PX,
      Math.min(DOT_MAX_WIDTH_PX, availableWidth / totalItems)
    )
  }

  const openGalleryView = (image: number, event: { target: any }) => {
    const GalleryModalContent = () => (
      <FullScreenGallery
        isOpen
        onClose={closeModal}
        images={itemsGallery}
        initialIndex={image}
      />
    )

    openModal({
      content: <GalleryModalContent />,
      position: 'bottom',
      height: '100vh',
    })

    const eventTrack = makeEventTrack({
      eventAction: event.target.ariaLabel,
      eventPage: 'pagina de produto',
    })

    sendEvent(eventTrack)
  }

  const itemsGallery: Array<TImage | Video> = video
    ? [...images.slice(0, 1), video, ...images.slice(1)]
    : images

  const dotWidthPx = getDotWidthPx(itemsGallery.length)
  const dotsTrackWidthPx = Math.min(
    DOTS_MAX_TRACK_WIDTH_PX,
    dotWidthPx * itemsGallery.length + (itemsGallery.length - 1) * DOT_GAP_PX
  )

  function processItems(processItem: Array<TImage | Video>, limit: number) {
    return processItem.slice(0, limit).map((item) => {
      if ('url' in item) {
        return { ...item }
      }

      return { ...item, imageUrl: item.imageUrl }
    })
  }

  return itemsGallery.length ? (
    <div
      data-testid="image-gallery-mobile"
      className={galleryContainerClassName}
    >
      <div className={STYLES.root}>
        <GalleryFlags />
        <Carousel
          clickCallback={(item: number, event: any) =>
            openGalleryView(item, event)
          }
          arrows={false}
          hasFirstImagePriority
          items={processItems(itemsGallery, itemsGallery.length)}
          dots={false}
          width={425}
          height={425}
          slideWidthPercentage={isFirstFoldRevampEnabled ? 84 : 100}
          alignLastSlideToEnd={isFirstFoldRevampEnabled}
          sectionClassName={
            isFirstFoldRevampEnabled ? 'carousel--first-fold-peek' : undefined
          }
          renderCustomContent={(item) => {
            if ('url' in item) {
              return <GalleryMovie {...item} mode="grid" />
            }

            return null
          }}
          onSlideChange={(currentIndex: number) => {
            setSelectedImageForClick(currentIndex)
          }}
        />
        {isPresale && (
          <div className={STYLES.preSaleFlag}>
            <PreSaleFlag />
          </div>
        )}
        {!isFirstFoldRevampEnabled && (
          <div className={STYLES.wishlistButton}>
            <WishListButton mobileSize="16" desktopSize="16" variant="pdp" />
          </div>
        )}
        {isFirstFoldRevampEnabled && itemsGallery.length > 1 && (
          <div
            className={STYLES.dotsContainer}
            style={{ width: `${dotsTrackWidthPx}px` }}
          >
            {itemsGallery.map((_, index) => (
              <button
                type="button"
                key={index}
                data-testid="gallery-first-fold-dot"
                aria-hidden="true"
                disabled
                className={
                  index === selectedImageForClick
                    ? STYLES.dotActive
                    : STYLES.dotInactive
                }
                style={{ width: `${dotWidthPx}px` }}
              />
            ))}
          </div>
        )}
        <div className={morePicturesClassName}>
          {!isFirstFoldRevampEnabled && (
            <ButtonMorePictures
              numberOfImages={itemsGallery.length}
              openGalleryView={() =>
                openGalleryView(selectedImageForClick, {
                  target: { ariaLabel: 'Ver mais fotos' },
                })
              }
              activeImage={selectedImageForClick + 1}
            />
          )}
        </div>
      </div>

      {isFirstFoldRevampEnabled && filteredBenefits.length > 0 && (
        <GalleryProductBenefits
          productBenefits={filteredBenefits}
          variant="mobile-first-fold"
        />
      )}
    </div>
  ) : (
    <GallerySkeleton loading images={images} />
  )
}
